import axios from 'axios';
import appInsights from '../../../lib/app-insights';
import EncryptionHelper from '../../../lib/encryptionHelper';
import { getFreezeState } from '../../../lib/adminHelper';

const env = process.env.REACT_APP_ENV as string;
const examSessionApiUrl = process.env.REACT_APP_EXAM_SESSION_API_URL as string;

export let candidateExamInfo = {
  fullName: '',
  examName: '',
  examTimeUTC: '',
  scheduledStartTime: undefined,
  isWebAI: false,
  isProproctorAI: false,
  isProproctorLive: false,
  isSecCameraRequired: false,
  address: undefined,
  eligibilityCode: '',
  deliveryMode: '',
  examCode: '',
  programCode: '',
  clientCode: '',
  examId: '',
  confirmationNumber: '',
  examConfig: undefined,
};

export const getCandidateExamInfo = async (
  confirmationNumber: string,
  lastName: string,
  locale: string,
) => {
  let token = await getAccessToken(confirmationNumber, lastName);
  let examSession = await getExamSession(
    token.commands.examSessionId,
    token.commands.access_token,
  );
  let examConfiguration = await getExamConfiguration(
    examSession.exam.examId,
    token.commands.access_token,
  );

  return getCandidateInfo(
    examSession,
    examConfiguration,
    confirmationNumber,
    locale,
  );
};

export async function checkFreezeState(
  confirmationNumber: string,
  lastName?: string,
): Promise<boolean> {
  try {
    const freezeState = await getFreezeState();
    if (freezeState.isFreezeActive) {
      const payload = {
        CandidateIdentifier: confirmationNumber,
        Environment: env,
        ApplicationSurface: 'Candidate Portal',
        Reason: 'freeze activated',
        UTCTimestamp: new Date().toISOString(),
      };
      const message = `Freeze state blocked candidate login ${JSON.stringify(payload)}`;
      appInsights.logWarning(message);
      return true;
    }
    return freezeState.isFreezeActive;
  } catch (err: any) {
    appInsights.logError(
          `Freeze state check failed on login | ConfirmationCode: ${confirmationNumber} | LastName: ${lastName} | Environment: ${env}`
        );
    return false;
  }
}

export async function getAccessToken(
  confirmationNumber: string,
  lastName: string,
): Promise<any> {
  let data = `{"ConfirmationCode":"${confirmationNumber}","LastName":"${lastName}"}`;
  let config = {
    method: 'post',
    mode: 'cors',
    maxBodyLength: Infinity,
    url: `${examSessionApiUrl}/identity-service/token`,
    headers: {
      env: `${env}`,
      'Content-Type': 'text/plain',
    },
    data: data,
  };

  try {
    var response = await axios.request(config);
    if (response.status === 200) {
      appInsights.logInfo(
        `Successfully retrieved access token for confirmation number:: ${confirmationNumber}`,
      );
      return response.data;
    } else {
      appInsights.logError(
        `Unexted status code when getting access token :: ${response.status} :: ${response.statusText}`,
      );
      throw response;
    }
  } catch (err: any) {
    appInsights.logError(
      `Failed to retrieved access token for confirmation number:: ${confirmationNumber}::${err.message}`,
    );
    throw err;
  }
}

export async function getExamSession(
  examSessionId: string,
  accessToken: string,
) {
  let config = {
    method: 'get',
    maxBodyLength: Infinity,
    url: `${examSessionApiUrl}/exam-sessions/${examSessionId}/candidate-session-info`,
    headers: { 
      'env': `${env}`,  
      'Authorization': `Bearer ${accessToken}`,
    },
    data: '',
  };

  try {
    var response = await axios.request(config);
    if (response.status === 200) {
      appInsights.logInfo(
        `Successfully retrieved exam session for exam session id::${examSessionId}`,
      );
      const encryptionHelper = new EncryptionHelper();
      return encryptionHelper.decryptData(response.data.payload);
    } else {
      appInsights.logError(
        `Unexted status code when getting exam session :: ${response.status} :: ${response.statusText}`,
      );
      throw response;
    }
  } catch (err: any) {
    appInsights.logError(
      `Failed to retrieved exam session for exam session id:: ${examSessionId}::${err.message}`,
    );
    throw err;
  }
}

export async function getExamConfiguration(
  examId: string,
  accessToken: string,
) {
  let config = {
    method: 'get',
    maxBodyLength: Infinity,
    url: `${examSessionApiUrl}/exam-configuration/${examId}`,
    headers: { 
      'env': `${env}`, 
      'Authorization': `Bearer ${accessToken}`
    },
    data: '',
  };

  try {
    var response = await axios.request(config);
    if (response.status === 200) {
      appInsights.logInfo(
        `Successfully retrieved exam configuration for exam id:: ${examId}`,
      );
      return response.data;
    } else {
      appInsights.logError(
        `Unexted status code when getting exam configuration :: ${response.status} :: ${response.statusText}`,
      );
      throw response;
    }
  } catch (err: any) {
    appInsights.logError(
      `Failed to retrieved exam configuration for exam id:: ${examId}::${err.message}`,
    );
    throw err;
  }
}

export function getCandidateInfo(
  examSession: any,
  examConfiguration: any,
  confirmationNumber: string,
  locale: string,
) {
  candidateExamInfo.fullName = examSession.candidateName;
  candidateExamInfo.examName = examSession.exam.examName;
  candidateExamInfo.scheduledStartTime = examSession.exam.scheduledStartTime;
  candidateExamInfo.examTimeUTC = examSession.exam.startTimeLocal;
  candidateExamInfo.address = examSession.address;
  candidateExamInfo.eligibilityCode = examSession.eligibilityCode;
  candidateExamInfo.deliveryMode = examSession.exam.deliveryMode;
  candidateExamInfo.examCode = examSession.exam.examCode;
  candidateExamInfo.programCode = examSession.exam.programCode;
  candidateExamInfo.clientCode = examSession.exam.clientCode;
  candidateExamInfo.examId = examSession.exam.examId;
  candidateExamInfo.confirmationNumber = confirmationNumber;
  candidateExamInfo.examConfig = examConfiguration;

  if (examConfiguration.environmentCamera) {
    candidateExamInfo.isSecCameraRequired = true;
    return candidateExamInfo;
  }

  if (!examConfiguration.autoProctoringConfiguration.isAutoProctoring) {
    candidateExamInfo.isProproctorLive = true;
  } else {
    if (examConfiguration.deliveryOption.isWebEnabled) {
      candidateExamInfo.isWebAI = true;
    } else {
      candidateExamInfo.isProproctorAI = true;
    }
  }
  return candidateExamInfo;
}
